<script setup>

import Header from '../header/Header.vue';
import Editor from '../editor/Editor.vue';
import Chat from './Chat.vue';
import Footer from '../editor/Footer.vue';
import LayoutHandle from './LayoutHandle.vue';
import { computed, inject, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import DocumentList from './DocumentList.vue';
import Icon from '../Icon.vue';
import Button from '../components/Button.vue';
import { LocalProps } from '../localProps';
import SidePusher from '../components/SidePusher.vue';
import { Events } from '../events';
import Toaster from './Toaster.vue';
import DocumentHistoryDialog from './history/DocumentHistoryDialog.vue';
import { navigate, newThread } from '../actions/documentActions';
import Messenger from '../chat/Messenger.vue';
import { providedRef } from '../composables/common';
import Modal from '../Modal.vue';
import ConfirmationDialog from './ConfirmationDialog.vue';
import Tiptap from './Tiptap.vue';
import UIBlock from '../utils/uiBlock/UIBlock.vue';
import BillingDialog from '@/payments/BillingDialog.vue';
import Dialogs from '../dialogs/Dialogs.vue';
import WorkbenchWizardCanvasSwtich from './WorkbenchWizardCanvasSwtich.vue';
import { animatedPlaceholder } from '../chat/animatedPlaceholder';
import BillingIssueDialog from './BillingIssueDialog.vue';

const responsiveChat = ref(false)

const activeDocument = inject('document')
const isWizard = inject('wizardMode');
const draftText = providedRef('draftText', null);
const draftGenerated = providedRef('draftGenerated', false);
const touched = ref(false)

const confirmationTask = providedRef('confirmationTask', null)
const billingIssue = providedRef('billingIssue', null)
const showBillingDialog = providedRef('showBillingDialog', false)

const activeDialog = ref(null)

provide('showDialog', name => activeDialog.value = name)

watch(isWizard, () => {
  if (isWizard.value) {
    draftText.value = null
    draftGenerated.value = false
    touched.value = false
    LocalProps.set(LocalProps.SIDEBAR, false)
  }
}, {immediate: true});

// this is diff view
const newDocumentVersionDraft = inject('newDocumentVersionDraft')
watch(newDocumentVersionDraft, () => {
  if (newDocumentVersionDraft.value) {
    responsiveChat.value = false
  }
});

function onAgentEvent(event, stopPropagation) {
  if (event.type === 'tool-call-request') {
    if (event.name === 'GenerateDocumentFromTemplate' && isWizard.value) {
      draftGenerated.value = false
      draftText.value = ''
      isWizard.value = false  
    }    
  }

  if (event.type === 'tool-call-start') {
    if (event.name === 'ExtractPlaceholders') {
    }
  }

  if (event.type === 'token' && draftText.value !== null && event.run === 'document-generator') {
    stopPropagation();
    draftText.value += event.token
    if (draftText.value.length < 100) {
      draftText.value = draftText.value.replace(/^`{1,3}[a-z]*\n?/i, '')
    }
    return ''
  }  
}

function onChatDone(messages) {
  if (draftText.value !== null) {
    draftGenerated.value = true
  }
}

function testTransition() {
  draftGenerated.value = false
  draftText.value = ''
  isWizard.value = false  
}

onMounted(() => {
  const sp_prompt = localStorage.getItem('sp_prompt')
  if (sp_prompt) {
    Events.dispatch(Events.CHAT_QUESTION, { 
      prompt: sp_prompt
    })
  }
  localStorage.removeItem('sp_prompt')
})

</script>


<template>
  <div class="workbench" :data-theme="isWizard ? 'light' : 'dark'">

    <Dialogs v-model="activeDialog" />
    <UIBlock />
    <ConfirmationDialog />  
    <BillingIssueDialog />  
    <Toaster />

    
      
    <Header class="bg-base-300" v-if="isWizard">
      <template #leftExtention>
        <Button :title="responsiveChat ? 'hide AI chat' : 'show AI chat'"  @action="responsiveChat = !responsiveChat" class="btn-ghost btn-sm p-1 mx-1 responsiveModeBtn">
          <Icon :name="responsiveChat ? 'fluent--textbox-16-regular' : 'ion--sparkles-sharp'" :size="20"/>
        </Button>                
      </template>
    </Header>

    <Header v-else >
      <template #leftExtention>
        <Button :title="responsiveChat ? 'hide AI chat' : 'show AI chat'"  @action="responsiveChat = !responsiveChat" class="btn-ghost btn-sm p-1 mx-1 responsiveModeBtn">
          <Icon :name="responsiveChat ? 'fluent--textbox-16-regular' : 'ion--sparkles-sharp'" :size="20"/>
        </Button>                
      </template>
    </Header>

    <div 
      style="position: relative; display: flex; flex: 1; max-width: 100vw;"
      :class="`responsive-mode-${responsiveChat ? 'chat' : 'editor'}`"
    >
      <SidePusher />   
      <WorkbenchWizardCanvasSwtich 
        :isWizard="isWizard" 
        :affordenceMode='isWizard && !touched' 
        :activeDocument="activeDocument" 
      >
      <Messenger 
          @send="touched = true"
          @agent="onAgentEvent"
          @done="onChatDone"
          :hideMessages="isWizard && !touched"
          :activeDocument="activeDocument" 
          :simple-mode="isWizard" 
          :agent="isWizard ? 'document-creation' : undefined"
          :animatedPlaceholder="isWizard && !touched"
          :placeholder="isWizard ? 'Tell me about the document you creating...' : undefined"
        >
      </Messenger>

      </WorkbenchWizardCanvasSwtich>
    </div>  
    <Footer />
  </div>

</template>

<style scoped lang="less">
  .workbench {
    height: 100%;    
    display: flex;
    flex-direction: column;  
    --editor-border-border-radius: 10px;
  }

  .main-chat {

    display: flex;    
    flex-direction: column;
    gap: 10px;    
    background: var(--color-base-300);
    flex-shrink: 1;    
    overflow: auto;

    @media (max-width: (@light-mode-bp - 1px)) { 
      width: unset !important; 
      position: absolute;
      z-index: 800;
      top: 0; left: 0; bottom: 0; right: 0;
      max-width: 500px;
      transform: translateX(-100%);      
      transition: transform 0.3s ease-out;
      .responsive-mode-chat & {
        display: flex;
        // visibility: visible;
        transform: translateX(0);  
      }
    }
  }

  @media (max-width: (@light-mode-bp - 1px)) { 
    .layout-handle {
      visibility: hidden;
    }
  }

  .responsiveModeBtn { 
    display: flex;
    @media (min-width: @light-mode-bp) {
      & {
        display: none;
      }
    }
  }

  .slide-up-enter-active {
    transition: all 0.3s ease-out;
  }
  
  .slide-up-leave-active {
    
  }

  .slide-up-enter-from, .slide-up-leave-to{
    opacity: 0;
  }

  .slide-up-enter-from {
    transform: translateX(100px);  
  }

  .slide-up-leave-to {
    transform: translateX(-100px);
  }
</style>
